import { useEffect, useState } from 'react'
import { useAppStore } from '@/store/app'
import { currentPushSubscription, disablePush, enablePush, notificationPermission, pushSupported } from '@/lib/push'
import { playSound, readPrefs, writePrefs, type NotifyPrefs } from '@/lib/notify'
import { isDesktop } from '@/lib/desktop'
import layer from './SettingsLayer.module.css'
import styles from './VoiceSection.module.css'

/** Настройки «Уведомления»: системные уведомления, звук и Web Push при закрытой вкладке. */
export function NotificationsSection() {
  const [prefs, setPrefs] = useState<NotifyPrefs>(readPrefs)
  const [permission, setPermission] = useState(notificationPermission())
  const [subscribed, setSubscribed] = useState<boolean | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const me = useAppStore((s) => s.currentUser)

  useEffect(() => {
    let cancelled = false
    void currentPushSubscription().then((sub) => {
      if (!cancelled) setSubscribed(Boolean(sub))
    })
    return () => {
      cancelled = true
    }
  }, [])

  const update = (patch: Partial<NotifyPrefs>) => {
    const next = { ...prefs, ...patch }
    setPrefs(next)
    writePrefs(next)
  }

  const askPermission = async () => {
    if (typeof Notification === 'undefined') return
    const p = await Notification.requestPermission()
    setPermission(p)
    if (p !== 'granted') setError(`Разрешение на уведомления не выдано. Включите их в настройках ${isDesktop ? 'системы' : 'сайта'}`)
  }

  const toggleDesktop = async (on: boolean) => {
    setError(null)
    if (on && permission !== 'granted') await askPermission()
    update({ desktop: on })
  }

  const togglePush = async (on: boolean) => {
    setError(null)
    setBusy(true)
    try {
      if (on) {
        await enablePush()
        setSubscribed(true)
      } else {
        await disablePush()
        setSubscribed(false)
      }
      update({ push: on })
      setPermission(notificationPermission())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось изменить подписку')
      update({ push: false })
    } finally {
      setBusy(false)
    }
  }

  const test = () => {
    if (prefs.sound) playSound()
    if (permission === 'granted') {
      try {
        const n = new Notification('Кличат', { body: `${me?.global_name ?? me?.username ?? 'Вы'}: так выглядит уведомление`, icon: '/icon-192.png', silent: true })
        setTimeout(() => n.close(), 5000)
      } catch {
        setError('Уведомления не показываются, пока окно открыто. Push, когда вас нет в сети, всё равно придёт')
      }
    }
  }

  const permissionText =
    permission === 'granted' ? 'разрешены' : permission === 'denied' ? 'запрещены' : permission === 'unsupported' ? 'не поддерживаются' : 'ещё не запрашивались'

  return (
    <section>
      <h1 className={layer.sectionTitle}>Уведомления</h1>
      <h2 className={layer.subTitle}>Когда Кличат открыт</h2>
      <div className={styles.toggles}>
        <label className={styles.toggle}>
          <input type="checkbox" checked={prefs.desktop} onChange={(e) => void toggleDesktop(e.target.checked)} />
          <span className={styles.toggleText}>
            <span className={styles.toggleLabel}>Системные уведомления</span>
            <span className={styles.toggleHint}>Когда окно не в фокусе: личные сообщения и упоминания с учётом настроек серверов. Сейчас {permissionText}.</span>
          </span>
        </label>
        <label className={styles.toggle}>
          <input type="checkbox" checked={prefs.sound} onChange={(e) => update({ sound: e.target.checked })} />
          <span className={styles.toggleText}>
            <span className={styles.toggleLabel}>Звук нового сообщения</span>
            <span className={styles.toggleHint}>Короткий сигнал, если сообщение пришло не в открытый канал</span>
          </span>
        </label>
      </div>
      <div className={styles.meterRow}>
        <button type="button" className={layer.button} onClick={test}>Проверить</button>
        {permission !== 'granted' && permission !== 'unsupported' ? (
          <button type="button" className={styles.link} onClick={() => void askPermission()}>Разрешить уведомления</button>
        ) : null}
      </div>

      <h2 className={layer.subTitle}>Когда вас нет в сети</h2>
      <div className={styles.toggles}>
        <label className={styles.toggle}>
          <input type="checkbox" checked={prefs.push && subscribed !== false} disabled={busy || !pushSupported() || subscribed === null} onChange={(e) => void togglePush(e.target.checked)} />
          <span className={styles.toggleText}>
            <span className={styles.toggleLabel}>Push-уведомления на этом устройстве</span>
            <span className={styles.toggleHint}>
              {pushSupported()
                ? 'Личные сообщения и упоминания приходят, пока вас нет в сети. Включается отдельно на каждом устройстве.'
                : 'Недоступно: здесь нет поддержки push-уведомлений или соединение не защищено.'}
            </span>
          </span>
        </label>
      </div>
      {error ? <p className={styles.error}>{error}</p> : null}
      <p className={layer.text}>Уровень уведомлений для сервера и канала («все сообщения», «только упоминания», «ничего») и заглушение настраиваются в меню сервера и канала.</p>
    </section>
  )
}
